// Re-export the simone chat components for use as the workspace AI
// drawer. Single source of truth: extensions/packages/simone owns
// the Chat / ChatMessage / ChatMarkdown / SessionMenu / TargetPicker /
// PickedContextChip / ConnectionBadge components; this file just
// surfaces them inside workspace's shared component registry so
// app.js can mount them by bare-key auto-extend.
//
// The simone surface reads state keys we DON'T own:
//   bridgeStatus, bridgeUrl, agent, chatInput, chatMessages,
//   chatTyping, picking, pickedElements, showSettings,
//   currentAssistantId, sessions, activeSessionId, target,
//   targetSelected, targetOptions, targetDraft*, cliAuth,
//   cloudWorkspaces, recentPairings, currentOrigin
// These are declared in workspace/state-app.js with workspace-safe
// defaults (bridgeStatus: 'open' so the composer is enabled even
// though we don't have a real bridge — sdk.ai is our transport).
//
// The simone surface dispatches via window.__simoneSendOverBridge.
// workspace installs an adapter at shell boot (functions/simoneAiTransport)
// that wires that handle through sdk.ai.dispatch so the user's picker
// selection (simone/providers/local) is honored.

// VENDORED from extensions/packages/simone/symbols/components/ into
// ./_simone/. simone is `private: true` (never published), so the bare
// `@symbo-ls/simone/...` import resolved only via the local monorepo
// symlink — it broke the CI `smbls build` (workspace checks out alone),
// which fast-failed every dev.my deploy. In-tree relative imports keep
// esbuild's symlink-preserving resolver on the workspace tree AND build
// in CI. Re-sync ./_simone/ from simone source if those components change.
export { Chat } from './_simone/Chat.js'
// chatScopeOf/ck are Chat.js-internal helpers (state-shape resolution,
// composer-key builder) with no external caller today. Both are already
// frank-safe (ck's own header comment documents why it was rewritten to be
// self-contained instead of calling chatScopeOf) — re-exported here so
// they're part of the published component surface, same barrel, same
// pattern as everything else in this file, rather than dropped-export
// debt with no consumer to point at.
export { chatScopeOf, ck } from './_simone/Chat.js'
export { ChatMessage } from './_simone/ChatMessage.js'
// Interactive cards the assistant attaches to its messages (tickets, people/
// agents, thumbnails, choice/confirm). Rendered by ChatMessage.Cards.
export { ChatCard } from './_simone/ChatCard.js'
// ChatSourceChip — ONE cited source under an assistant answer (spec §4.2);
// ChatConfidenceFactor — ONE weighted factor of its confidence breakdown
// (spec §4.3). Both are mounted by ChatMessage through `childExtends:
// '<Name>'`, so they MUST be registered here: a string key resolves through
// the merged context, and an unregistered name renders an empty div.
export { ChatSourceChip } from './_simone/ChatSourceChip.js'
export { ChatConfidenceFactor } from './_simone/ChatConfidenceFactor.js'
export { ChatMarkdown } from './_simone/ChatMarkdown.js'
// collapseCodeFences is a ChatMarkdown.js-internal formatting helper, same
// re-export rationale as chatScopeOf/ck above.
export { collapseCodeFences } from './_simone/ChatMarkdown.js'
export { ToolCallCard } from './_simone/ToolCallCard.js'
// ChatToolRow — one row of the assistant's live agentic timeline
// (message.tools, fed by simoneAiTransport tool.activity frames).
export { ChatToolRow } from './_simone/ChatToolRow.js'
// ChatToolStepLabel — a step's words alone, the row's own text factory (the
// island's "Working: …" head and the approval bubble name a step with it).
export { ChatToolStepLabel } from './_simone/ChatToolRow.js'
// ChatToolStep — a timeline step with the question it asked under it
// (ChatMessage.Activity's `childExtends: 'ChatToolStep'`).
export { ChatToolStep } from './_simone/ChatToolStep.js'
// SimoneReasoningPill — a compact "AI is thinking" indicator above the composer,
// shown when a general turn is live but the drawer is closed (e.g. a page is open).
export { SimoneReasoningPill } from './_simone/ReasoningPill.js'
// AiBubbleStack — the words above a bar as bubbles, and what waits on the
// person, over the island and over a composer hosting a call.
export { AiBubbleStack, AiCaptionBubble } from './_simone/AiBubbleStack.js'
// AiAsk — what waits on the person: a step for the OK (Run · Skip · Run all)
// or a question for a pick (its options), with its live region — the ONE ask,
// above a bar (AiBubbleStack) and on the full-screen voice call (VoiceOverlay).
export { AiAsk } from './_simone/AiAsk.js'
// SimoneSuggestions — contextual "apply changes on what's open" chips above the
// composer, shown when idle and the current page/record has actionable suggestions.
export { SimoneSuggestions } from './_simone/Suggestions.js'
// The starter-prompt row the Chat's empty state lists (`childExtends:
// 'AssistantStarter'`) — registered here so every host that mounts Chat
// (workspace drawer, standalone simone, ext-ui) resolves the key.
export { AssistantStarter, AssistantStarterList } from './_simone/AssistantStarter.js'
export { ToolTerminal } from './_simone/ToolTerminal.js'
export { SessionMenu } from './_simone/SessionMenu.js'
export { ConnectionBadge } from './_simone/ConnectionBadge.js'
// PickedContextChip RETIRED 2026-08-16 (O1, opus.md): picked elements render
// through the ONE awareness bar (AiContextBar, mounted inside the composer's
// PromptTextarea) as the 'attached' tier, alongside focus. See AiContextBar.js.
export { SettingsDrawer } from './_simone/SettingsDrawer.js'
// TargetPicker has bridge-specific deps (cloud workspace picker hits
// the symbols api directly). Skip it in workspace — the host's
// ParamsBtn dropdown is already the mode picker.
// SettingsDrawer is also bridge-config specific.
// PickedContextChip / inspector flow doesn't apply outside the
// chrome-extension surface — workspace state keeps `picking: false` and
// `pickedElements: []` as permanent defaults so the chip stays empty.
// sendPrompt is registered in workspace/functions/index.js (functions
// belong in context.functions, not context.components).

// @-agent mention helpers for the composer textarea (see mention.js header
// for the caret/splice contract). No caller in this checkout yet — the
// composer doesn't wire the trigger up — but they're finished, tested
// (mention.test.js), and live under components/_simone/ like everything
// else in this file, so they're re-exported here rather than left as
// dropped-export debt with no barrel path to reach them.
export {
  detectAgentMention,
  spliceAt,
  agentInsertText,
  agentRoster,
  agentsFromMongo,
  filterAgentsForMention
} from './_simone/mention.js'
